// AISuggestChip — Laya SUGGEST chip for category suggestion (W3.1 §7.1).
//
// 7 visual states per spec:
//   - idle (default outline button)
//   - loading (spinner)
//   - success (high/mid band) — gold-tint fill + confidence badge
//   - success-low (low band) — same shape but with 「較低」 caveat
//   - reject (band=reject) — warning tone
//   - error (503/network) — chip returns to idle + caller surfaces InfoBanner
//   - re-analyse — user already picked a category; offer re-run

import { useState } from "react";
import { Sparkles, Loader2, AlertCircle, Check, RefreshCw } from "lucide-react";

import { useLayaClassifyCategory } from "@/lib/hooks/anniversary";
import { isLayaEnabled } from "@/lib/layaFlag";
import { categoryLabelZh } from "@/lib/anniversary/categoryIcon";
import type { AnniversaryClassifyResponse } from "@/lib/api/anniversary";
import { cn } from "@/lib/utils/cn";

export type AISuggestState =
  | "idle"
  | "loading"
  | "success-high"
  | "success-mid"
  | "success-low"
  | "reject"
  | "error"
  | "reanalyse";

export interface AISuggestChipProps {
  title: string;
  description?: string | null;
  /** Current user-confirmed category (drives 「reanalyse」 state). */
  currentCategory?: string | null;
  /** Fired with the Laya result when user clicks 「採用」. */
  onAccept: (response: AnniversaryClassifyResponse) => void;
  /** Optional callback when error happens (caller surfaces InfoBanner). */
  onError?: (message: string) => void;
  className?: string;
}

function confidenceLabel(confidence: number): string {
  return `${Math.round(confidence * 100)}% 信心`;
}

export function AISuggestChip({
  title,
  description,
  currentCategory,
  onAccept,
  onError,
  className,
}: AISuggestChipProps) {
  const [result, setResult] = useState<AnniversaryClassifyResponse | null>(null);
  const classify = useLayaClassifyCategory();

  // W49.0 §5. Not rendered at all while the flag is off — see `lib/layaFlag.ts`.
  // This component is a button whose only behaviour is a request the MVP
  // backend answers with 503, so every state below it is a dead end wearing a
  // button's affordances.
  if (!isLayaEnabled()) return null;

  const run = async () => {
    if (!title || title.trim().length === 0) return;
    try {
      const r = await classify.mutateAsync({
        title: title.trim(),
        description: description ?? null,
      });
      setResult(r);
    } catch (err) {
      // 503 / network → caller shows InfoBanner; chip returns to idle.
      const msg = err instanceof Error ? err.message : "功能暫時無法使用";
      onError?.(msg);
      setResult(null);
    }
  };

  // R0 (W39.0): the tappable states measured 214×32 — `h-8` put a primary
  // action 12px under the 44pt floor. `min-h-11` is the *tap* floor and leaves
  // the pill's visual weight alone; the button states below pair it with
  // `min-w-11` so the class itself is the evidence `expectTouchTarget` reads.
  // The `reject` state is a `<div>`, not a control, so it deliberately opts out
  // of the size floor rather than claiming a target it does not have.
  const baseClass = cn(
    "inline-flex items-center gap-1.5 min-h-11 px-3 rounded-pill text-meta",
    "border transition-colors duration-quick ease-paper",
    "press focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
    className,
  );

  /** Add to `baseClass` only for the states that are actually a `<button>`. */
  const tappableClass = "min-w-11";

  // Loading state
  if (classify.isPending) {
    return (
      <button
        type="button"
        disabled
        data-component="AISuggestChip"
        data-state="loading"
        className={cn(baseClass, tappableClass, "bg-paper-elevated border-hairline text-ink-2 cursor-progress")}
      >
        <Loader2 size={14} aria-hidden className="animate-spin" />
        <span>分析中…</span>
      </button>
    );
  }

  // Success (high/mid/low) state — chip becomes a filled button.
  if (
    result &&
    (result.band === "high" || result.band === "mid" || result.band === "low")
  ) {
    const state: AISuggestState =
      result.band === "high"
        ? "success-high"
        : result.band === "mid"
          ? "success-mid"
          : "success-low";
    const caveat =
      result.band === "low" ? "信心度較低, 僅供參考" : null;
    return (
      <div
        data-component="AISuggestChip"
        data-state={state}
        data-band={result.band}
        className={cn(
          "inline-flex items-center gap-2 h-8 pl-2 pr-1 rounded-pill",
          "bg-gold-tint border border-gold/40 text-gold-ink",
        )}
      >
        <Sparkles size={14} aria-hidden />
        <span className="text-meta font-semibold">
          已建議:{categoryLabelZh(result.category)}
        </span>
        <span
          className="rounded-full bg-paper/60 px-1.5 py-0.5 text-micro text-ink-2"
          data-testid="ai-confidence"
        >
          {confidenceLabel(result.confidence)}
        </span>
        {caveat ? (
          <span className="text-micro text-ink-2" aria-live="polite">
            {caveat}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => onAccept(result)}
          data-testid="ai-accept"
          className="inline-flex items-center gap-1 h-6 px-2 rounded-pill bg-gold text-paper text-meta font-semibold press"
        >
          <Check size={12} aria-hidden />
          採用
        </button>
      </div>
    );
  }

  // Reject state — warning tone.
  if (result && result.band === "reject") {
    return (
      <div
        data-component="AISuggestChip"
        data-state="reject"
        className={cn(baseClass, "bg-partner-tint border-partner/40 text-partner-strong")}
      >
        <AlertCircle size={14} aria-hidden />
        <span>無法判定, 請手動選擇分類</span>
      </div>
    );
  }

  // Idle vs re-analyse: when user already picked a category, hint at re-run.
  if (currentCategory) {
    return (
      <button
        type="button"
        onClick={() => void run()}
        data-component="AISuggestChip"
        data-state="reanalyse"
        className={cn(baseClass, tappableClass, "bg-paper-elevated border-hairline text-ink-2 hover:bg-black/5")}
      >
        <RefreshCw size={14} aria-hidden />
        <span>依據當前選擇重新分析?</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void run()}
      data-component="AISuggestChip"
      data-state="idle"
      data-testid="ai-suggest-trigger"
      className={cn(baseClass, tappableClass, "bg-paper-elevated border-hairline text-ink-1 hover:bg-black/5")}
    >
      <Sparkles size={14} aria-hidden />
      <span>AI 建議分類</span>
    </button>
  );
}